import { useSignalEffect } from '@preact/signals'
import { useRef } from 'preact/hooks'
import { useClientBridge } from '../app/client-context.ts'
import { useView } from '../app/use-view.ts'
import styles from './cursor-stack.module.css'
import { useScreenUi } from './screen-context.ts'
import { Slot } from './slot.tsx'

/**
 * The stack held on the cursor: its icon and count at a slot's size, following the pointer
 * (or lifted over the focused slot with a gamepad). It takes no pointer events, so clicks
 * reach the slot underneath.
 */
export function CursorStack() {
  const ui = useScreenUi()
  const held = useView((view) => view.inventory?.screen?.cursor ?? null)
  const device = useView((view) => view.input.device)
  const ref = useRef<HTMLDivElement>(null)
  const { view } = useClientBridge()

  // Moves the element directly: a moving pointer re-renders nothing.
  useSignalEffect(() => {
    const element = ref.current
    const pointer = ui.pointer.value
    const focused = ui.hover.value
    const gamepad = view.value.input.device === 'gamepad'
    if (!element) return
    const at = gamepad && focused ? centreOf(focused.element.getBoundingClientRect()) : pointer
    element.style.translate = at ? `${String(at.x)}px ${String(at.y)}px` : ''
    element.dataset.placed = at ? 'true' : ''
  })

  // Always there, so the effect has it to place the moment a stack is taken.
  return (
    <div ref={ref} class={styles.cursor} data-device={device} aria-hidden="true">
      {held && (
        <div class={styles.lift}>
          <Slot slot={held} />
        </div>
      )}
    </div>
  )
}

function centreOf(rect: DOMRect): { x: number; y: number } {
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
}
